import React, { useState, useEffect, useCallback, useMemo } from "react";
import Order from "./Order";
import AssignModal from "./AssignModal";
import OrderDetailModal from "./OrderDetailModal";
import "../Css/SummaryOrder.css";
import Logger from "./Logger";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Menu, MenuItem } from "@mui/material";
import { getLoggerBaseURL } from '../utils/getLoggerBaseURL';

const SummaryOrder = () => {
  const [summaries, setSummaries] = useState([]);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [selectedOption, setSelectedOption] = useState(null);
  const adminEmail = sessionStorage.getItem("adminEmail");
  const email = adminEmail;
  const [showModal, setShowModal] = useState(false);
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [actionType, setActionType] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedSummary, setSelectedSummary] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");

  // Initialize Logger
  const logger = useMemo(() => new Logger(getLoggerBaseURL()), []);

  // Fetching summary orders based on admin email
  useEffect(() => {
    const fetchSummaries = async () => {
      try {
        const response = await fetch(`/api/packer/getAllSummary/${email}`);
        if (!response.ok) throw new Error("Network response was not ok");
        const data = await response.json();
        setSummaries(data.summaries || []);
      } catch (error) {
        logger.log(`Error fetching summaries: ${error.message}`, "ERROR");
      }
    };

    if (adminEmail) {
      fetchSummaries();
    }
  }, [adminEmail, email, logger]);

  const handleAssignClick = useCallback(
    (summary) => {
      // Find the summary object from the summaries array using Summary
      const selectedSummary = summaries.find(
        (item) => item.Summary === summary
      );

      if (
        selectedSummary &&
        Array.isArray(selectedSummary.details) &&
        selectedSummary.details.length > 0
      ) {
        const orderNumbers = selectedSummary.details.flatMap((detailGroup) =>
          detailGroup
            .map((detail) => detail.Order_No)
            .filter((orderNo) => orderNo)
        );
        setSelectedOrders(orderNumbers);
        setActionType("assign");
        setShowModal(true);
      } else {
        logger.log("Details for the selected summary are not in the expected format.", "ERROR");
      }
    },
    [summaries, logger]
  );

  const handleReassignClick = useCallback((summaryObj) => {
    const selectedSummary = summaryObj;

    if (
      selectedSummary &&
      Array.isArray(selectedSummary.details) &&
      selectedSummary.details.length > 0
    ) {
      const orderNumbers = selectedSummary.details.flatMap((detailGroup) =>
        detailGroup
          .map((detail) => detail.Order_No)
          .filter((orderNo) => orderNo)
      );
      setSelectedOrders(orderNumbers); // Store the correct order numbers
      setActionType("reassign");
      setShowModal(true);
    } else {
      logger.log("Details for the selected summary are not in the expected format.", "ERROR");
    }
  }, [logger]);

  const filteredSummaries = (() => {
    if (!Array.isArray(summaries)) {
      return [];
    }

    return summaries.filter((order) => {
      // Apply search filter based on Summary Number for all selected options
      const matchesSearchQuery =
        order.Summary &&
        order.Summary.toLowerCase().includes(searchQuery.toLowerCase());

      return matchesSearchQuery;
    });
  })();

  // Handle closing the menu
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Handle closing the modal
  const handleCloseModal = () => {
    setShowModal(false);
  };

  // Handle menu click
  const handleClick = (event, summaryObj) => {
    setAnchorEl(event.currentTarget);
    setSelectedSummary(summaryObj);
  };

  const handleStatusUpdated = useCallback(() => {
    // Fetch the updated orders data from the backend
    const fetchUpdatedOrders = async () => {
      try {
        const response = await fetch(`/api/packer/getAllSummary/${email}`);
        if (!response.ok) {
          throw new Error(`HTTP error! Status: ${response.status}`);
        }
        const data = await response.json();
        // Check if data contains summaries array and set it
        if (data && Array.isArray(data.summaries)) {
          setSummaries(data.summaries);
        } else {
          setSummaries([]);
        }
      } catch (error) {
        logger.log(`Error fetching updated data: ${error.message}`, "ERROR");
      }
    };

    fetchUpdatedOrders();
  }, [email, logger]);

  const handleAssignmentComplete = useCallback(() => {
    setSelectedOrders([]); // Reset the selected orders after assignment
  }, [setSelectedOrders]);

  return (
    <div className="Summary">
      <Order
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        selectedOption={selectedOption}
        setSelectedOption={setSelectedOption}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
      />
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <strong>Summary Number</strong>
              </TableCell>
              <TableCell>
                <strong>Driver</strong>
              </TableCell>
              <TableCell>
                <strong>Actions</strong>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {summaries &&
              summaries.length > 0 &&
              filteredSummaries.map((summaryObj, index) => {
                // Check if there are any orders with o_status '0' or '3' in the details of this summary
                const hasAssignableOrder = summaryObj.details.some(
                  (detail) =>
                    detail[0].o_status === "0" || detail[0].o_status === "3"
                );
                // Get the common Driv_Email if available from any of the orders in this summary
                const commonDrivName =
                  summaryObj.details.length > 0
                    ? summaryObj.details[0][3].name
                    : "";

                return (
                  <TableRow key={index}>
                    <TableCell>{summaryObj.Summary}</TableCell>{" "}
                    <TableCell>
                      <td>
                        {hasAssignableOrder ? (
                          <button
                            className="assignD"
                            onClick={() =>
                              handleAssignClick(summaryObj.Summary)
                            }
                            style={{ cursor: "pointer" }}
                          >
                            +Assign
                          </button>
                        ) : (
                          <span className="assigned">
                            {commonDrivName || "N/A"}
                          </span>
                        )}
                      </td>
                    </TableCell>
                    <TableCell>
                      <td>
                        <button className="Dbutton" onClick={(event) => handleClick(event, summaryObj)}>
                          ...
                        </button>
                      </td>
                      <Menu
                        id="simple-menu"
                        anchorEl={anchorEl}
                        keepMounted
                        open={
                          Boolean(anchorEl) && selectedSummary === summaryObj
                        }
                        onClose={handleClose}
                      >
                        {/* Conditionally render the Reassign button only for the selected summary */}
                        {selectedSummary &&
                          selectedSummary.Summary === summaryObj.Summary && (
                            <MenuItem
                              onClick={() => handleReassignClick(summaryObj)}
                            >
                              Reassign
                            </MenuItem>
                          )}

                        <MenuItem
                          onClick={() => {
                            setDetailModalOpen(true);
                            handleClose();
                            setSelectedSummary(summaryObj.Summary);
                          }}
                        >
                          View Detail
                        </MenuItem>
                      </Menu>
                    </TableCell>
                  </TableRow>
                );
              })}
          </TableBody>
        </Table>
      </TableContainer>

      {showModal && (
        <AssignModal
          showModal={showModal}
          onClose={handleCloseModal}
          selectedOrders={selectedOrders} // Pass selected orders to AssignModal
          actionType={actionType}
          onAssignmentComplete={handleAssignmentComplete}
          onStatusUpdated={handleStatusUpdated}
        />
      )}

      {/* Conditionally render the OrderDetailModal */}
      {detailModalOpen && (
        <OrderDetailModal
          open={detailModalOpen}
          handleClose={() => setDetailModalOpen(false)}
          summary={selectedSummary}
          email={email}
        />
      )}
    </div>
  );
};

export default SummaryOrder;
